// The intake form (architecture plan §7.1, §11.7; build plan P4). Field design is card quality:
// the category-specific follow-ups exist because the card standard needs exact identifiers.
// Labels, required markers, error association, and a credentials warning that cannot be missed.

import {
  BUSINESS_IMPACTS,
  type CreateRequestBodyInput,
  type FollowUpField,
  getCategory,
  type Me,
  PHONE_ROUTE_SENTENCE,
  portalReceivableCategories,
  REQUEST_CATEGORIES,
} from "@hoffman-support/contracts";
import { type FormEvent, useMemo, useState } from "react";
import { ApiError, createRequest } from "../api";
import { staffRaise } from "../staff-api";

const IMPACT_LABELS: Record<(typeof BUSINESS_IMPACTS)[number], string> = {
  individual: "Just me",
  multiple_people: "Several people",
  company_wide: "Company-wide",
};

type Errors = Record<string, string>;

function fieldError(errors: Errors, path: string) {
  const message = errors[path];
  return message ? (
    <p id={`${path}-error`} role="alert" style={{ color: "#a4000f", margin: "0.25rem 0 0" }}>
      {message}
    </p>
  ) : null;
}

function FollowUp({
  field,
  value,
  onChange,
  errors,
}: {
  field: FollowUpField;
  value: unknown;
  onChange: (v: unknown) => void;
  errors: Errors;
}) {
  const id = `fu-${field.id}`;
  const path = `followUpAnswers.${field.id}`;
  const describedBy = [`${id}-why`, errors[path] ? `${path}-error` : undefined]
    .filter(Boolean)
    .join(" ");
  const common = {
    id,
    name: field.id,
    required: field.required,
    "aria-describedby": describedBy,
    "aria-invalid": Boolean(errors[path]),
  };
  return (
    <div style={{ marginBottom: "1rem" }}>
      <label htmlFor={id}>
        {field.label}
        {field.required ? " *" : ""}
      </label>
      <br />
      {field.type === "select" ? (
        <select
          {...common}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Choose…</option>
          {field.options?.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : field.type === "boolean" ? (
        <input
          {...common}
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
      ) : field.type === "date" ? (
        <input
          {...common}
          type="date"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : field.type === "textarea" ? (
        <textarea
          {...common}
          rows={3}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
        />
      ) : (
        <input
          {...common}
          type="text"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          spellCheck={field.type !== "identifier"}
        />
      )}
      <p id={`${id}-why`} style={{ fontSize: "0.85rem", color: "#555", margin: "0.25rem 0 0" }}>
        {field.whyItMatters}
      </p>
      {fieldError(errors, path)}
    </div>
  );
}

/**
 * `requester` is the self-service form. `staff` is the call-taker raising on behalf of a caller
 * (§11.3): every configured category is allowed, including the phone-only ones, and the caller's
 * UPN plus how identity was verified are required so the request binds at their first sign-in.
 */
export interface RequestFormProps {
  me: Me;
  mode?: "requester" | "staff";
  /** An explicitly verified handoff supplies identity and submission outside the phone flow. */
  submitRequest?: (idempotencyKey: string, body: CreateRequestBodyInput) => Promise<void>;
  initialSubject?: string;
  submitDisabled?: boolean;
  heading?: string;
  submitLabel?: string;
}

export function RequestForm({
  me,
  mode = "requester",
  submitRequest,
  initialSubject = "",
  submitDisabled = false,
  heading,
  submitLabel,
}: RequestFormProps) {
  const phoneIntake = mode === "staff" && !submitRequest;
  const categories = useMemo(
    () => (phoneIntake ? REQUEST_CATEGORIES : portalReceivableCategories()),
    [phoneIntake],
  );
  const [caller, setCaller] = useState({ upn: "", displayName: "", verification: "" });
  // One key per form instance, so a retry after a network failure cannot create a second request.
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [category, setCategory] = useState("");
  const [fields, setFields] = useState({
    subject: initialSubject,
    description: "",
    department: "",
    location: "",
    onsiteOrRemote: "onsite" as "onsite" | "remote",
    callback: "",
    affectedSystemOrDevice: "",
    onset: "",
    errorText: "",
    troubleshootingAttempted: "",
    businessImpact: "individual" as (typeof BUSINESS_IMPACTS)[number],
    neededBy: "",
    sponsor: "",
    sensitivityAcknowledged: false,
  });
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<
    | { kind: "idle" }
    | { kind: "submitting" }
    | { kind: "rejected"; message: string }
    | { kind: "failed"; message: string }
  >({ kind: "idle" });

  const selected = category ? getCategory(category) : undefined;

  function set<K extends keyof typeof fields>(key: K, value: (typeof fields)[K]) {
    setFields((f) => ({ ...f, [key]: value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submitDisabled || status.kind === "submitting") return;
    setErrors({});
    setStatus({ kind: "submitting" });
    const body: CreateRequestBodyInput = {
      category,
      subject: fields.subject,
      description: fields.description,
      department: fields.department,
      location: fields.location,
      onsiteOrRemote: fields.onsiteOrRemote,
      callback: fields.callback,
      affectedSystemOrDevice: fields.affectedSystemOrDevice,
      onset: fields.onset,
      errorText: fields.errorText,
      troubleshootingAttempted: fields.troubleshootingAttempted,
      businessImpact: fields.businessImpact,
      neededBy: fields.neededBy || null,
      sponsor: fields.sponsor || null,
      sensitivityAcknowledged: fields.sensitivityAcknowledged as true,
      followUpAnswers: answers,
    };
    try {
      if (submitRequest) {
        await submitRequest(idempotencyKey, body);
        setStatus({ kind: "idle" });
        return;
      }
      if (mode === "staff") {
        const created = await staffRaise(me, idempotencyKey, {
          ...body,
          caller: {
            upn: caller.upn,
            ...(caller.displayName ? { displayName: caller.displayName } : {}),
            verification: caller.verification,
          },
        });
        window.location.assign(
          `/staff/requests/${created.publicId}?created=1${created.bound ? "&bound=1" : ""}${created.redacted ? "&redacted=1" : ""}`,
        );
        return;
      }
      const created = await createRequest(me, idempotencyKey, body);
      window.location.assign(
        `/requests/${created.publicId}?created=1${created.redacted ? "&redacted=1" : ""}`,
      );
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.failure.code === "credential_detected") {
          setStatus({ kind: "rejected", message: error.failure.message });
          return;
        }
        if (error.failure.issues) {
          const next: Errors = {};
          for (const issue of error.failure.issues) next[issue.path] = issue.message;
          setErrors(next);
          setStatus({ kind: "failed", message: "Some fields need attention." });
          return;
        }
        setStatus({ kind: "failed", message: error.failure.message });
        return;
      }
      setStatus({
        kind: "failed",
        message:
          "The request could not be sent. Check your connection and try again; your entries are kept.",
      });
    }
  }

  const text = (
    key: keyof typeof fields,
    label: string,
    opts: { required?: boolean; multiline?: boolean; type?: string; hint?: string } = {},
  ) => {
    const id = `f-${key}`;
    const describedBy = [
      opts.hint ? `${id}-hint` : undefined,
      errors[key] ? `${key}-error` : undefined,
    ]
      .filter(Boolean)
      .join(" ");
    const value = String(fields[key] ?? "");
    return (
      <div style={{ marginBottom: "1rem" }}>
        <label htmlFor={id}>
          {label}
          {opts.required ? " *" : ""}
        </label>
        <br />
        {opts.multiline ? (
          <textarea
            id={id}
            name={key}
            rows={4}
            required={opts.required}
            value={value}
            aria-describedby={describedBy}
            aria-invalid={Boolean(errors[key])}
            onChange={(e) => set(key, e.target.value as never)}
            style={{ width: "100%" }}
          />
        ) : (
          <input
            id={id}
            name={key}
            type={opts.type ?? "text"}
            required={opts.required}
            value={value}
            aria-describedby={describedBy}
            aria-invalid={Boolean(errors[key])}
            onChange={(e) => set(key, e.target.value as never)}
            style={{ width: "100%" }}
          />
        )}
        {opts.hint ? (
          <p
            id={`${id}-hint`}
            style={{ fontSize: "0.85rem", color: "#555", margin: "0.25rem 0 0" }}
          >
            {opts.hint}
          </p>
        ) : null}
        {fieldError(errors, key)}
      </div>
    );
  };

  return (
    <form onSubmit={submit} aria-labelledby="new-request-heading" noValidate>
      <h2 id="new-request-heading">
        {heading ?? (phoneIntake ? "Raise a request for a caller" : "Raise a request")}
      </h2>
      <p
        role="note"
        style={{ border: "2px solid #a4000f", padding: "0.75rem", background: "#fff4f4" }}
      >
        <strong>
          Do not enter passwords, MFA or verification codes, recovery keys, private keys, or
          sensitive customer or financial data.
        </strong>{" "}
        A submission that contains one is refused, and the value must be changed. Paste exact error
        text, IDs, and serial numbers freely; those are what a technician needs.
      </p>
      {phoneIntake ? (
        <fieldset style={{ marginBottom: "1rem" }}>
          <legend>Who is calling? *</legend>
          <p style={{ fontSize: "0.85rem", color: "#555" }}>
            The request is filed under the caller's work account and becomes theirs the first time
            they sign in. Record how you verified who they are; the binding expires if they do not
            sign in within 30 days.
          </p>
          <div style={{ marginBottom: "0.75rem" }}>
            <label htmlFor="f-caller-upn">Caller's work email (UPN) *</label>
            <br />
            <input
              id="f-caller-upn"
              type="email"
              required
              value={caller.upn}
              aria-invalid={Boolean(errors["caller.upn"])}
              onChange={(e) => setCaller((c) => ({ ...c, upn: e.target.value }))}
              style={{ width: "100%" }}
            />
            {fieldError(errors, "caller.upn")}
          </div>
          <div style={{ marginBottom: "0.75rem" }}>
            <label htmlFor="f-caller-name">Caller's name</label>
            <br />
            <input
              id="f-caller-name"
              type="text"
              value={caller.displayName}
              onChange={(e) => setCaller((c) => ({ ...c, displayName: e.target.value }))}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label htmlFor="f-caller-verification">How did you verify their identity? *</label>
            <br />
            <input
              id="f-caller-verification"
              type="text"
              required
              value={caller.verification}
              aria-invalid={Boolean(errors["caller.verification"])}
              onChange={(e) => setCaller((c) => ({ ...c, verification: e.target.value }))}
              placeholder="Called back on the directory number; manager confirmed; …"
              style={{ width: "100%" }}
            />
            {fieldError(errors, "caller.verification")}
          </div>
        </fieldset>
      ) : (
        <p>{PHONE_ROUTE_SENTENCE}</p>
      )}

      <div style={{ marginBottom: "1rem" }}>
        <label htmlFor="f-category">What is this about? *</label>
        <br />
        <select
          id="f-category"
          name="category"
          required
          value={category}
          aria-invalid={Boolean(errors.category)}
          aria-describedby={errors.category ? "category-error" : undefined}
          onChange={(e) => {
            setCategory(e.target.value);
            setAnswers({});
          }}
        >
          <option value="">Choose a category…</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
        {selected ? (
          <p style={{ fontSize: "0.85rem", color: "#555" }}>{selected.description}</p>
        ) : null}
        {fieldError(errors, "category")}
      </div>

      {text("subject", "Subject", {
        required: true,
        hint: "One line, specific: the device, the application, and what is wrong.",
      })}
      {text("description", "What happened", { required: true, multiline: true })}

      {selected ? (
        <fieldset style={{ marginBottom: "1rem" }}>
          <legend>About your {selected.label.toLowerCase()} problem</legend>
          {selected.followUpFields.map((field) => (
            <FollowUp
              key={field.id}
              field={field}
              value={answers[field.id]}
              onChange={(v) => setAnswers((a) => ({ ...a, [field.id]: v }))}
              errors={errors}
            />
          ))}
        </fieldset>
      ) : null}

      {text("affectedSystemOrDevice", "Affected system or device", {
        hint: "Device name or serial, application and version, server, or share.",
      })}
      {text("onset", "When it started")}
      {text("errorText", "Exact error text", {
        multiline: true,
        hint: "Paste it verbatim, including any correlation or request IDs.",
      })}
      {text("troubleshootingAttempted", "What you have already tried", { multiline: true })}
      {text("department", "Department", { required: true })}
      {text("location", "Location", { required: true })}

      <fieldset style={{ marginBottom: "1rem" }}>
        <legend>Where are you working? *</legend>
        {(["onsite", "remote"] as const).map((v) => (
          <label key={v} style={{ marginRight: "1rem" }}>
            <input
              type="radio"
              name="onsiteOrRemote"
              value={v}
              checked={fields.onsiteOrRemote === v}
              onChange={() => set("onsiteOrRemote", v)}
            />{" "}
            {v === "onsite" ? "On site" : "Remote"}
          </label>
        ))}
      </fieldset>

      {text("callback", "Best callback number", { required: true, type: "tel" })}

      <div style={{ marginBottom: "1rem" }}>
        <label htmlFor="f-impact">Who is affected? *</label>
        <br />
        <select
          id="f-impact"
          name="businessImpact"
          value={fields.businessImpact}
          onChange={(e) =>
            set("businessImpact", e.target.value as (typeof BUSINESS_IMPACTS)[number])
          }
        >
          {BUSINESS_IMPACTS.map((v) => (
            <option key={v} value={v}>
              {IMPACT_LABELS[v]}
            </option>
          ))}
        </select>
      </div>

      {text("neededBy", "Needed by", { type: "date", hint: "Only when there is a real deadline." })}
      {text("sponsor", "Sponsor or approver", {
        hint: "Only when someone else has to approve this.",
      })}

      <div style={{ marginBottom: "1rem" }}>
        <label>
          <input
            type="checkbox"
            name="sensitivityAcknowledged"
            required
            checked={fields.sensitivityAcknowledged}
            aria-invalid={Boolean(errors.sensitivityAcknowledged)}
            onChange={(e) => set("sensitivityAcknowledged", e.target.checked)}
          />{" "}
          I confirm this request contains no passwords, codes, keys, or sensitive customer or
          financial data. *
        </label>
        {fieldError(errors, "sensitivityAcknowledged")}
      </div>

      <p role="status" aria-live="polite">
        {status.kind === "submitting" && "Sending your request…"}
        {status.kind === "failed" && status.message}
      </p>
      {status.kind === "rejected" ? (
        <div
          role="alert"
          style={{
            border: "2px solid #a4000f",
            padding: "0.75rem",
            background: "#fff4f4",
            marginBottom: "1rem",
          }}
        >
          <strong>The request was not sent.</strong> {status.message}
        </div>
      ) : null}
      <button type="submit" disabled={submitDisabled || status.kind === "submitting"}>
        {submitLabel ?? "Send request"}
      </button>
    </form>
  );
}
